import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  Skeleton,
} from '@mui/material'
import { DrawerState } from './Lines'
import { ArrowForward, Save, InfoOutlined, InfoOutline } from '@mui/icons-material'
import './HeaderCRMDetails.css'
import { DatePicker, repCodeType } from '@impres-development/assets/client'
import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import { CRMSearch, flattenBundledStructure, getRepCodeLabel } from '../../helper'
import { usePaymentTerms, useRepCodes, useUser } from '../../hooks'
import { quoteType, salesforceAccountType, salesforceContactType, salesforceOpportunityType } from '../../types'
import { CatalogRow } from 'src/CatalogList/CatalogList.types'
import { NewOpportunityDialog } from '../../QuoteDetails/QuoteDetailsOptionsDialogs'
import { BundleLineItem } from './lineItemPricing'

function formatOpportunityValue(value: string | string[] | null | undefined) {
  if (value == null || value === '') return '-'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
  return value
}

function DataRow({ label, value, loading }: { label: string; value: string | undefined; loading: boolean }) {
  return (
    <Grid size={6} key={label}>
      <div>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Box>{loading ? <Skeleton variant="text" width={100} height={20} /> : <Typography>{value}</Typography>}</Box>
      </div>
    </Grid>
  )
}

export default function HeaderCRMDetails({
  setDrawerState,
  quoteId,
  lineItems = [],
  onSaved,
  crmOnly = false,
}: {
  setDrawerState: (state: DrawerState) => void
  quoteId: string | undefined
  lineItems?: BundleLineItem[]
  onSaved?: () => void
  crmOnly?: boolean
}) {
  const user = useUser()
  const [repCodes] = useRepCodes()
  const paymentTerms = usePaymentTerms()

  const [quote, setQuote] = useState<quoteType>({} as quoteType)
  const [editQuoteCopy, setEditQuoteCopy] = useState<quoteType>({ GovPayTerms: '' } as quoteType)
  const [crmOpportunity, setCrmOpportunity] = useState<null | salesforceOpportunityType>(null)
  const [crmAccount, setCrmAccount] = useState<null | salesforceAccountType>(null)
  const [crmContact, setCrmContact] = useState<null | salesforceContactType>(null)
  const [selectedOpp, setSelectedOpp] = useState<null | { id: string; label: string }>(null)
  const [selectedAccount, setSelectedAccount] = useState<null | { id: string; label: string }>(null)
  const [selectedContact, setSelectedContact] = useState<null | { id: string; label: string }>(null)
  const [contractList, setContractList] = useState<CatalogRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<null | string>(null)
  const [newOpportunityOpen, setNewOpportunityOpen] = useState(false)

  useEffect(() => {
    if (!user.email) return

    axios.get('/contract').then(res => {
      setContractList(
        res.data.list
          .filter((c: CatalogRow) => c.status === 'Active' && c.parentId === null)
          .sort((a: CatalogRow, b: CatalogRow) => a.contractName.localeCompare(b.contractName)),
      )
    })
  }, [user.email])

  useEffect(() => {
    if (!user.email || !quoteId) return

    setLoading(true)
    axios
      .get(`/quote/${quoteId}`)
      .then(res => {
        setQuote(res.data.quote)
        setEditQuoteCopy({
          ...res.data.quote,
          CRMAccount: res.data.crmAccount?.Id ?? null,
          CRMContact: res.data.crmContact?.Id ?? null,
        })
        setCrmOpportunity(res.data.crmOpportunity)
        setCrmAccount(res.data.crmAccount)
        setCrmContact(res.data.crmContact)
        setSelectedOpp(
          res.data.crmOpportunity ? { id: res.data.crmOpportunity.Id, label: res.data.crmOpportunity.Name } : null,
        )
        setSelectedAccount(res.data.crmAccount ? { id: res.data.crmAccount.Id, label: res.data.crmAccount.Name } : null)
        setSelectedContact(res.data.crmContact ? { id: res.data.crmContact.Id, label: res.data.crmContact.Name } : null)
      })
      .catch(err => {
        console.error(err)
        setSaveError('Failed to load quote details. Please try again or contact support if the problem persists.')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [user.email, quoteId])

  const handleSyncSalesforceQuote = (contactId?: string | null) => {
    const flatLines = flattenBundledStructure(lineItems)
    return axios.put(`/quote/${quoteId}/syncSalesforce`, {
      quote: {
        OpportunityId: selectedOpp?.id,
        I_Quote__c: `${quote.QuoteNumber}v${quote.Version}`,
        Name: `${quote.QuoteNumber}v${quote.Version}`,
        RFQ__c: quote.RFQNumber,
        RFQ_Due_Date__c: quote.RFQDueDate,
        Contract_Vehicle__c: quote.ContractVehicle,
        Contract_Number__c: quote.ContractNumber,
        Quote_Date__c: quote.QuoteDate,
        ExpirationDate: quote.ExpirationDate,
        Status: quote.Status,
        ContactId: contactId === undefined ? selectedContact?.id : contactId,
      },
      quoteLines: flatLines
        .filter(line => line.LineNumber !== -1)
        .map(line => ({
          IMPRES_LineNum__c: line.LineNumber,
          Distributor__c: line.Provider,
          Manufacturer__c: line.Manufacturer,
          Mfr_PartNum__c: line.MfrPartNumber,
          Display_PartNum__c: line.DisplayPartNumber,
          Description__c: line.Description,
          QTY__c: line.Qty ?? 0,
          Quantity: 1,
          Unit_Cost__c: line.UnitCost,
          Ext_Cost__c: line.ExtCost,
          OEM_Code__c: line.OEMCode,
          Validation__c: line.CLIN,
          UnitPrice: line.UnitPrice ?? 0,
          Product_Type__c: line.ProductType,
          Start_Date__c: line.StartDate,
          End_Date__c: line.EndDate,
          Service_Tag__c: line.ServiceTag,
          SerialNum__c: line.LicenseSN,
          Notes__c: line.Notes,
          TAA__c: line.TAA,
          Country_of_Origin__c: line.CountryOfOrigin,
          Contract_Ceiling_Price__c: line.ContractCeilingPrice,
          Contract_Catalog__c: line.ContractCatalog,
          ContractNum__c: line.ContractNumber,
          Unit_of_Measure__c: line.UnitOfMeasure,
          IMPRES_List_Price__c: line.MSRP,
        })),
    })
  }

  const applyOpportunityResponse = (opp: salesforceOpportunityType) => {
    if (opp.Account?.Id !== selectedAccount?.id) {
      setSelectedContact(null)
    }
    setCrmOpportunity(opp)
    setSelectedAccount(opp.Account ? { id: opp.Account.Id, label: opp.Account.Name } : null)

    if (selectedOpp?.id !== crmOpportunity?.Id) {
      handleSyncSalesforceQuote(opp.Account?.Id === selectedAccount?.id ? undefined : null).catch(err => {
        console.error(err)
      })
    }
  }

  const handleSave = () => {
    if (!quoteId) return

    setSaveError(null)
    setSaving(true)

    Promise.all([
      axios
        .put(`/quote/${quoteId}`, {
          quote: {
            ContractVehicle: editQuoteCopy.ContractVehicle,
            ContractNumber: editQuoteCopy.ContractNumber,
            ExpirationDate: editQuoteCopy.ExpirationDate,
            RFQNumber: editQuoteCopy.RFQNumber,
            RFQDueDate: editQuoteCopy.RFQDueDate,
            QuoteDate: editQuoteCopy.QuoteDate,
            QuoteDescription: editQuoteCopy.QuoteDescription,
            GovPayTerms: editQuoteCopy.GovPayTerms,
            Name: editQuoteCopy.Name,
            RepCode: editQuoteCopy.RepCode,
            ...(editQuoteCopy.CRMOpportunity !== quote.CRMOpportunity
              ? { CRMOpportunity: editQuoteCopy.CRMOpportunity }
              : {}),
            CRMAccount: selectedAccount?.id,
            CRMContact: selectedContact?.id,
            Owner: editQuoteCopy.Owner,
          },
        })
        .catch(err => {
          setSaveError(
            'Main Quote Information failed to save. Please try again or contact support if the problem persists.',
          )
          return Promise.reject(err.response.data.error)
        }),
      (selectedAccount?.id !== crmOpportunity?.Account?.Id ||
        selectedContact?.id !== crmOpportunity?.Contact?.Id ||
        editQuoteCopy.Owner !== crmOpportunity?.Owner.Email) &&
      editQuoteCopy.CRMOpportunity
        ? axios
            .put(`/opportunity/${editQuoteCopy.CRMOpportunity}`, {
              opportunity: {
                AccountId: selectedAccount?.id,
                Owner: {
                  Email: editQuoteCopy.Owner,
                },
              },
              quoteId: quote.QuoteId,
              quote: {
                ContactId: selectedContact?.id,
                Name: editQuoteCopy.Name,
                OpportunityId: selectedOpp?.id,
              },
            })
            .catch(err => {
              console.error(err.response.data.error)
              setSaveError(
                'Opportunity information failed to update. Please try again or contact support if the problem persists.',
              )
              return Promise.reject(err)
            })
        : Promise.resolve(null),
    ])
      .then(([quoteRes, oppRes]) => {
        setQuote(editQuoteCopy)
        if (oppRes) {
          applyOpportunityResponse(oppRes.data)
        } else if (quoteRes.data) {
          applyOpportunityResponse(quoteRes.data)
        }
        onSaved?.()
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const displayAccount = useMemo(
    () => (selectedAccount?.id && selectedAccount.id === crmAccount?.Id ? crmAccount : null),
    [selectedAccount, crmAccount],
  )
  const displayOpportunity = useMemo(
    () => (selectedOpp?.id && selectedOpp.id === crmOpportunity?.Id ? crmOpportunity : null),
    [selectedOpp, crmOpportunity],
  )
  const displayContact = useMemo(
    () => (selectedContact?.id && selectedContact.id === crmContact?.Id ? crmContact : null),
    [selectedContact, crmContact],
  )

  return (
    <div className="header-crm-details">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{crmOnly ? 'CRM Details' : 'Header & CRM Details'}</Typography>
        <div className="header-crm-actions">
          {!crmOnly && (
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<Save />}
              loading={saving}
              disabled={loading || saving}
            >
              Save Changes
            </Button>
          )}
          <IconButton onClick={() => setDrawerState({ type: null })}>
            <ArrowForward />
          </IconButton>
        </div>
      </Box>
      <Divider orientation="horizontal" />
      {!crmOnly && (
        <>
          <Grid container spacing={2} className="header-crm-details-box">
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Quote Owner *"
                size="small"
                disabled={loading}
                value={editQuoteCopy.Owner ?? ''}
                onChange={e => {
                  setEditQuoteCopy(prev => ({ ...prev, RepCode: '', Owner: e.target.value }))
                }}
              >
                {Array.from(
                  new Map(
                    repCodes
                      .filter(
                        (repCode): repCode is typeof repCode & { IsrEmail: string } =>
                          !!repCode.IsActive && !!repCode.IsrEmail,
                      )
                      .map(repCode => [repCode.IsrEmail, repCode]),
                  ).values(),
                ).map(repCode => (
                  <MenuItem value={repCode.IsrEmail} key={repCode.IsrEmail}>
                    {repCode.InsideSalesRep}
                  </MenuItem>
                ))}
                {!repCodes.map(repCode => repCode.IsrEmail).includes(editQuoteCopy.Owner ?? '') &&
                  editQuoteCopy.Owner && (
                    <MenuItem value={editQuoteCopy.Owner} key={editQuoteCopy.Owner}>
                      {editQuoteCopy.Owner}
                    </MenuItem>
                  )}
              </TextField>
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Account"
                value={selectedAccount}
                onChange={(e, value) => {
                  setSelectedAccount(value)
                  setSelectedOpp(null)
                  setSelectedContact(null)
                  setEditQuoteCopy(prev => ({
                    ...prev,
                    CRMAccount: value?.id ?? null,
                    CRMContact: null,
                    CRMOpportunity: null,
                  }))
                }}
                label="Account Name *"
                disabled={loading}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                disabled={loading}
                label="Contract Vehicle *"
                size="small"
                value={editQuoteCopy.ContractVehicle ?? ''}
                onChange={e =>
                  setEditQuoteCopy(prev => ({
                    ...prev,
                    ContractVehicle: e.target.value,
                    ContractNumber: contractList.find(c => c.contractVehicle === e.target.value)?.contractNumber ?? '',
                  }))
                }
              >
                {contractList.map(contract => (
                  <MenuItem value={contract.contractVehicle} key={contract.contractVehicle}>
                    {contract.contractVehicle}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <div className="header-crm-opportunity-row">
                <CRMSearch
                  object="Opportunity"
                  label="CRM Opportunity"
                  accountId={selectedAccount?.id}
                  disabled={loading || !selectedAccount?.id}
                  value={selectedOpp}
                  onChange={(e, value) => {
                    setSelectedOpp(value)
                    setEditQuoteCopy(prev => ({ ...prev, CRMOpportunity: value?.id ?? null }))
                    if (value?.accountId) {
                      if (value.accountId !== selectedAccount?.id) {
                        setSelectedContact(null)
                      }
                      setSelectedAccount({ id: value.accountId, label: value.accountName ?? '' })
                    }
                  }}
                />
                <Button variant="outlined" size="medium" disabled={loading} onClick={() => setNewOpportunityOpen(true)}>
                  Create
                </Button>
                <Tooltip title="Select CRM opportunity to link this quote to. Sync will occur every time quote is saved.">
                  <IconButton size="medium">
                    <InfoOutlined />
                  </IconButton>
                </Tooltip>
              </div>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Rep Code *"
                size="small"
                disabled={loading}
                value={repCodes.find(repCode => repCode.RepCode === editQuoteCopy.RepCode)?.RepCode ?? ''}
                onChange={e => setEditQuoteCopy(prev => ({ ...prev, RepCode: e.target.value }))}
              >
                {Array.from(
                  new Set([
                    repCodes.find(repCode => repCode.RepCode === editQuoteCopy.RepCode) ?? ({} as repCodeType),
                    ...repCodes.filter(
                      repCode =>
                        repCode.IsActive &&
                        (repCode.IsrEmail?.toLowerCase() === user.email?.toLowerCase() ||
                          repCode.AeEmail?.toLowerCase() === user.email?.toLowerCase()),
                    ),
                  ]),
                ).map(repCode => (
                  <MenuItem value={repCode.RepCode} key={repCode.RepCode}>
                    {getRepCodeLabel(repCode) || '-'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Contact"
                accountId={selectedAccount?.id}
                value={selectedContact}
                onChange={(e, value) => {
                  setSelectedContact(value)
                  setEditQuoteCopy(prev => ({ ...prev, CRMContact: value?.id ?? null }))
                }}
                label="CRM Contact"
                disabled={loading || !selectedAccount?.id}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="header-crm-details-box">
            <Grid size={6}>
              <TextField
                fullWidth
                disabled={loading}
                label="Quote Name"
                size="small"
                value={editQuoteCopy.Name ? editQuoteCopy.Name : ''}
                onChange={e => setEditQuoteCopy(prev => ({ ...prev, Name: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                disabled={loading}
                label="Quote Description"
                size="small"
                value={editQuoteCopy.QuoteDescription ?? ''}
                onChange={e => setEditQuoteCopy(prev => ({ ...prev, QuoteDescription: e.target.value }))}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={editQuoteCopy.QuoteDescription ?? ''}>
                          <InfoOutline />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                disabled={loading}
                label="Quote Date"
                size="small"
                value={editQuoteCopy.QuoteDate ? dayjs(editQuoteCopy.QuoteDate) : null}
                onChange={e =>
                  setEditQuoteCopy(prev => {
                    if (!e) return { ...prev, QuoteDate: null }
                    const gap =
                      prev.QuoteDate && prev.ExpirationDate
                        ? dayjs(prev.ExpirationDate).startOf('day').diff(dayjs(prev.QuoteDate).startOf('day'), 'day')
                        : 30
                    return {
                      ...prev,
                      QuoteDate: dayjs(e).toDate(),
                      ExpirationDate: dayjs(e).add(gap, 'day').toDate(),
                    }
                  })
                }
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                disabled={loading}
                label="Expiration Date"
                size="small"
                value={editQuoteCopy.ExpirationDate ? dayjs(editQuoteCopy.ExpirationDate) : null}
                onChange={e => setEditQuoteCopy(prev => ({ ...prev, ExpirationDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                disabled={loading}
                label="Terms"
                size="small"
                value={editQuoteCopy.GovPayTerms}
                onChange={e => setEditQuoteCopy(prev => ({ ...prev, GovPayTerms: e.target.value }))}
              >
                {paymentTerms.map(term => (
                  <MenuItem value={term.name} key={term.id}>
                    {term.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                label="RFQ #"
                size="small"
                fullWidth
                disabled={loading}
                value={editQuoteCopy.RFQNumber ?? ''}
                onChange={e => setEditQuoteCopy(prev => ({ ...prev, RFQNumber: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                label="RFQ Due Date"
                size="small"
                fullWidth
                disabled={loading}
                value={editQuoteCopy.RFQDueDate ? dayjs(editQuoteCopy.RFQDueDate) : null}
                onChange={e => setEditQuoteCopy(prev => ({ ...prev, RFQDueDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={2} className="header-crm-details-box header-crm-opportunity-data">
        <Grid size={12} className="header-crm-opportunity-data-row">
          <Typography variant="h6">CRM Account</Typography>
          <Typography variant="h6" color="text.secondary">
            {selectedAccount?.label ?? '-'}
          </Typography>
        </Grid>
        {(
          [
            {
              label: 'Parent Account',
              value: displayAccount?.Parent?.Name ?? '-',
            },
            {
              label: 'Top Level Account',
              value: displayAccount?.Top_Level_Account__c ?? '-',
            },
            { label: 'Account Owner', value: displayAccount?.Owner.Name ?? '-' },
            {
              label: 'DoD, Civ, ISG',
              value: displayAccount?.DoD_CIV_ISG__c ?? '-',
            },
          ] as const
        ).map(field => (
          <DataRow key={field.label} label={field.label} value={field.value} loading={loading} />
        ))}
      </Grid>

      <Grid container spacing={2} className="header-crm-details-box header-crm-opportunity-data">
        <Grid size={12} className="header-crm-opportunity-data-row">
          <Typography variant="h6">CRM Opportunity</Typography>
          <Typography variant="h6" color="text.secondary">
            {selectedOpp?.label ?? '-'}
          </Typography>
        </Grid>
        {(
          [
            {
              label: 'Close Date',
              value: displayOpportunity?.CloseDate ? dayjs(displayOpportunity.CloseDate).format('MM/DD/YYYY') : '-',
            },
            { label: 'Stage', value: displayOpportunity?.StageName ?? '-' },
            { label: 'Forecast Category', value: displayOpportunity?.ForecastCategoryName ?? '-' },
            {
              label: 'Core Competency',
              value: formatOpportunityValue(displayOpportunity?.Line_Of_Business__c),
            },
            {
              label: 'Set Asides',
              value: formatOpportunityValue(displayOpportunity?.Set_Asides__c),
            },
          ] as const
        ).map(field => (
          <DataRow key={field.label} label={field.label} value={field.value} loading={loading} />
        ))}
      </Grid>

      <Grid container spacing={2} className="header-crm-details-box header-crm-opportunity-data">
        <Grid size={12} className="header-crm-opportunity-data-row">
          <Typography variant="h6">CRM Contact Details</Typography>
        </Grid>
        {(
          [
            {
              label: 'Contact Name',
              value: selectedContact?.label ?? '-',
            },
            { label: 'Email', value: displayContact?.Email ?? '-' },
            { label: 'Phone', value: displayContact?.Phone ?? '-' },
          ] as const
        ).map(field => (
          <DataRow key={field.label} label={field.label} value={field.value} loading={loading} />
        ))}
        <Grid size={12} className="header-crm-opportunity-data-row">
          <Typography variant="h6">Default Billing Address</Typography>
        </Grid>
        {(
          [
            {
              label: 'Street',
              value: displayContact?.Default_Billing_Address__c?.street ?? '-',
            },
            { label: 'City', value: displayContact?.Default_Billing_Address__c?.city ?? '-' },
            { label: 'State', value: displayContact?.Default_Billing_Address__c?.state ?? '-' },
            { label: 'Zip', value: displayContact?.Default_Billing_Address__c?.postalCode ?? '-' },
          ] as const
        ).map(field => (
          <DataRow key={field.label} label={field.label} value={field.value} loading={loading} />
        ))}
        <Grid size={12} className="header-crm-opportunity-data-row">
          <Typography variant="h6">Default Shipping Address</Typography>
        </Grid>
        {(
          [
            { label: 'Street', value: displayContact?.Default_Shipping_Address__c?.street ?? '-' },
            { label: 'City', value: displayContact?.Default_Shipping_Address__c?.city ?? '-' },
            { label: 'State', value: displayContact?.Default_Shipping_Address__c?.state ?? '-' },
            { label: 'Zip', value: displayContact?.Default_Shipping_Address__c?.postalCode ?? '-' },
          ] as const
        ).map(field => (
          <DataRow key={field.label} label={field.label} value={field.value} loading={loading} />
        ))}
      </Grid>

      {!crmOnly && quoteId && (
        <NewOpportunityDialog
          open={newOpportunityOpen}
          quoteId={quoteId}
          setCrmOpportunity={opp => {
            setCrmOpportunity(opp)
            setEditQuoteCopy(prev => ({ ...prev, CRMOpportunity: opp.Id }))
          }}
          onClose={() => setNewOpportunityOpen(false)}
          setAccount={setSelectedAccount}
          setContact={setSelectedContact}
          setOpportunity={setSelectedOpp}
        />
      )}

      <Snackbar open={!!saveError}>
        <Alert severity="error" variant="filled" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      </Snackbar>
    </div>
  )
}
